import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../lib/api';
import type { ClientProfile, EventDetail, EventInput, EventKind, EventRow, Me, Tag } from '../lib/api';
import { daysUntil, fmtDate, fmtDateTime, titleCase } from '../lib/format';
import { Empty, Kpi, Modal, PageHead, Pill } from '../components/ui';
import { TagChips } from '../components/tags';
import { DecisionButtons, DecisionPill, ScoreBadge } from '../components/matches';

const KINDS: EventKind[] = ['conference', 'industry_day', 'proposers_day', 'tim', 'workshop', 'symposium', 'association', 'commercial', 'investor', 'convening', 'other'];
const KIND_LABEL: Record<string, string> = { tim: 'Technical interchange', industry_day: 'Industry day', proposers_day: 'Proposers day' };
const kindLabel = (k: string) => KIND_LABEL[k] ?? titleCase(k);
const ACTION_KIND: Record<string, 'ok' | 'warn' | 'info' | 'muted'> = { Exhibit: 'ok', Team: 'ok', Submit: 'warn', Speak: 'warn', Attend: 'info', Monitor: 'muted' };

export function Events({ params, me }: { params: string[]; me: Me }) {
  const routeId = params[0] === 'id' ? (params[1] ?? null) : null;
  const [data, setData] = useState<{ items: EventRow[]; counts: { total: number; upcoming: number; next_90_days: number } } | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [clients, setClients] = useState<ClientProfile[]>([]);
  const [upcoming, setUpcoming] = useState(true);
  const [kind, setKind] = useState('');
  const [tag, setTag] = useState('');
  const [client, setClient] = useState('');
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<string | null>(routeId);
  const canEdit = me.role !== 'viewer';

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (!upcoming) p.set('upcoming', 'all');
    if (kind) p.set('kind', kind);
    if (tag) p.set('tag', tag);
    if (client) p.set('client', client);
    if (q) p.set('q', q);
    api<{ items: EventRow[]; counts: { total: number; upcoming: number; next_90_days: number } }>(`/api/events?${p}`).then(setData).catch(() => setData(null));
  }, [upcoming, kind, tag, client, q]);
  useEffect(load, [load]);
  useEffect(() => {
    api<Tag[]>('/api/tags').then(setTags).catch(() => setTags([]));
    api<{ items: ClientProfile[] }>('/api/clients').then((r) => setClients(r.items)).catch(() => setClients([]));
  }, []);
  useEffect(() => { setSelected(routeId); }, [routeId]);
  const closeDetail = useCallback(() => { setSelected(null); if (routeId) location.hash = '/events'; }, [routeId]);

  const items = data?.items ?? [];
  const clientName = clients.find((c) => c.id === client)?.name;
  return (
    <>
      <PageHead
        title="Events"
        sub="Conferences, industry days, proposers days and convenings with their deadlines, and what each client should do about them"
        actions={
          <>
            <form className="search" onSubmit={(e: FormEvent) => { e.preventDefault(); setQ(draft.trim()); }} style={{ minWidth: 220 }}>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Name, description, city…" />
            </form>
            <label className="sort-control"><span className="muted sm">For</span>
              <select className="select" value={client} onChange={(e) => setClient(e.target.value)}>
                <option value="">All clients</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            {canEdit && <button className="primary-btn" onClick={() => setAdding(true)}>+ Add event</button>}
          </>
        }
      />
      <div className="kpi-row">
        <Kpi label="Upcoming events" value={data?.counts.upcoming ?? '…'} />
        <Kpi label="Starting within 90 days" value={data?.counts.next_90_days ?? '…'} />
        <Kpi label="Recommended to clients" value={items.filter((e) => e.recommended_for > 0).length} hint="score 50 or more" />
        <Kpi label="Deadlines ahead" value={items.filter((e) => e.next_deadline).length} hint="registration, abstract, exhibitor" />
      </div>
      <div className="filter-bar">
        <button className={`filter-btn ${upcoming ? 'active' : ''}`} onClick={() => setUpcoming(true)}>Upcoming</button>
        <button className={`filter-btn ${!upcoming ? 'active' : ''}`} onClick={() => setUpcoming(false)}>All including past</button>
        <span className="filter-gap" />
        <button className={`filter-btn ${kind === '' ? 'active' : ''}`} onClick={() => setKind('')}>All kinds</button>
        {KINDS.map((k) => <button key={k} className={`filter-btn ${kind === k ? 'active' : ''}`} onClick={() => setKind(k)}>{kindLabel(k)}</button>)}
      </div>
      <div className="filter-bar">
        <button className={`filter-btn ${tag === '' ? 'active' : ''}`} onClick={() => setTag('')}>Any technology</button>
        {tags.map((t) => <button key={t.id} className={`filter-btn ${tag === t.id ? 'active' : ''}`} onClick={() => setTag(t.id)}>{t.label}</button>)}
      </div>

      <div className="panel">
        <div className="panel-head"><h2>{data ? `${items.length} event${items.length === 1 ? '' : 's'}${clientName ? ` · recommendations for ${clientName}` : ''}` : 'Loading…'}</h2></div>
        <div className="panel-body table-wrap">
          {!data ? <Empty>Loading…</Empty> : items.length === 0 ? (
            <Empty>No events recorded{kind || tag || q ? ' for these filters' : ''}.{canEdit && ' Add the conferences, industry days and proposers days that matter; Praxis will score them for every client and alert on the deadlines.'}</Empty>
          ) : (
            <table className="data-table">
              <thead>
                <tr>{client && <><th>Fit</th><th>Do</th></>}<th>Event</th><th>Kind</th><th>When</th><th>Next deadline</th><th>Technology</th>{!client && <th className="num">Recommended</th>}</tr>
              </thead>
              <tbody>
                {items.map((e) => {
                  const dl = daysUntil(e.next_deadline);
                  return (
                    <tr key={e.id} className="clickable" onClick={() => setSelected(e.id)}>
                      {client && (
                        <>
                          <td onClick={(ev) => ev.stopPropagation()}>{e.match_id ? <><ScoreBadge score={e.fit} />{canEdit && <div style={{ marginTop: 6 }}><DecisionButtons matchId={e.match_id} decision={e.fit_decision ?? 'pending'} onChanged={load} /></div>}</> : <span className="muted sm">no fit</span>}</td>
                          <td>{e.recommendation ? <><Pill kind={ACTION_KIND[e.recommendation.action] ?? 'muted'}>{e.recommendation.action}</Pill>{e.recommendation.also.length > 0 && <div className="muted sm">also {e.recommendation.also.join(', ')}</div>}</> : <span className="muted">—</span>}</td>
                        </>
                      )}
                      <td><strong>{e.name}</strong><div className="muted sm">{[e.organizer_name, [e.city, e.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}{e.status === 'cancelled' && <span className="tag" style={{ background: 'rgba(251,113,133,.16)', color: 'var(--red)' }}>CANCELLED</span>}</div></td>
                      <td>{kindLabel(e.kind)}</td>
                      <td>{e.starts_on ? <>{fmtDate(e.starts_on)}{e.ends_on && e.ends_on !== e.starts_on ? <div className="muted sm">to {fmtDate(e.ends_on)}</div> : null}</> : <span className="muted">TBD</span>}</td>
                      <td>{e.next_deadline ? <>{fmtDate(e.next_deadline)}<div className={`sm ${dl !== null && dl <= 14 ? 'neg' : 'muted'}`}>{dl} days</div></> : <span className="muted">—</span>}</td>
                      <td><TagChips tags={e.tags} all={tags} max={3} /></td>
                      {!client && <td className="num">{e.recommended_for}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {adding && <EventForm tags={tags} onClose={() => setAdding(false)} onSaved={(id) => { setAdding(false); load(); setSelected(id); }} />}
      {selected && <EventModal id={selected} me={me} tags={tags} onClose={closeDetail} onChanged={load} />}
    </>
  );
}

type Draft = { name: string; kind: EventKind; starts_on: string; ends_on: string; city: string; state: string; url: string; description: string; registration_opens_on: string; registration_closes_on: string; abstract_due_on: string; speaker_due_on: string; exhibitor_due_on: string; status: string };
const fromDetail = (d?: EventDetail | null): Draft => ({
  name: d?.name ?? '', kind: d?.kind ?? 'conference', starts_on: d?.starts_on?.slice(0, 10) ?? '', ends_on: d?.ends_on?.slice(0, 10) ?? '', city: d?.city ?? '', state: d?.state ?? '', url: d?.url ?? '', description: d?.description ?? '',
  registration_opens_on: d?.registration_opens_on?.slice(0, 10) ?? '', registration_closes_on: d?.registration_closes_on?.slice(0, 10) ?? '', abstract_due_on: d?.abstract_due_on?.slice(0, 10) ?? '',
  speaker_due_on: d?.speaker_due_on?.slice(0, 10) ?? '', exhibitor_due_on: d?.exhibitor_due_on?.slice(0, 10) ?? '', status: d?.status ?? 'announced',
});

function EventForm({ tags, existing, onClose, onSaved }: { tags: Tag[]; existing?: EventDetail | null; onClose: () => void; onSaved: (id: string) => void }) {
  const [f, setF] = useState<Draft>(() => fromDetail(existing));
  const [picked, setPicked] = useState<string[]>(existing?.tags ?? []);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const set = (k: keyof Draft) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const dateOrNull = (s: string) => (s ? s : null);
  async function submit(e: FormEvent) {
    e.preventDefault(); setSaving(true); setErr(null);
    const body: EventInput = {
      name: f.name.trim(), kind: f.kind, starts_on: dateOrNull(f.starts_on), ends_on: dateOrNull(f.ends_on), city: f.city.trim() || undefined, state: f.state.trim() || undefined, url: f.url.trim() || undefined,
      description: f.description.trim() || undefined, registration_opens_on: dateOrNull(f.registration_opens_on), registration_closes_on: dateOrNull(f.registration_closes_on),
      abstract_due_on: dateOrNull(f.abstract_due_on), speaker_due_on: dateOrNull(f.speaker_due_on), exhibitor_due_on: dateOrNull(f.exhibitor_due_on), status: f.status, tags: picked,
    };
    try {
      if (existing) { await api(`/api/events/${existing.id}`, { method: 'PATCH', json: body }); onSaved(existing.id); }
      else { const r = await api<{ id: string }>('/api/events', { method: 'POST', json: body }); onSaved(r.id); }
    } catch (ex) { setErr((ex as Error).message); } finally { setSaving(false); }
  }
  const dateField = (k: keyof Draft, label: string) => <div className="field"><label htmlFor={`ev-${k}`}>{label}</label><input id={`ev-${k}`} className="input" type="date" value={f[k]} onChange={set(k)} /></div>;
  return (
    <Modal title={existing ? 'Edit event' : 'Add event'} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="form-grid">
          <div className="field span-2"><label htmlFor="ev-name">Name</label><input id="ev-name" className="input" required minLength={3} value={f.name} onChange={set('name')} /></div>
          <div className="field"><label htmlFor="ev-kind">Kind</label><select id="ev-kind" className="select" value={f.kind} onChange={set('kind')}>{KINDS.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}</select></div>
          <div className="field"><label htmlFor="ev-status">Status</label><select id="ev-status" className="select" value={f.status} onChange={set('status')}><option value="announced">Announced</option><option value="confirmed">Confirmed</option><option value="cancelled">Cancelled</option></select></div>
          {dateField('starts_on', 'Starts')}{dateField('ends_on', 'Ends')}
          <div className="field"><label htmlFor="ev-city">City</label><input id="ev-city" className="input" value={f.city} onChange={set('city')} /></div>
          <div className="field"><label htmlFor="ev-state">State</label><input id="ev-state" className="input" maxLength={10} value={f.state} onChange={set('state')} /></div>
          <div className="field span-2"><label htmlFor="ev-url">URL</label><input id="ev-url" className="input" value={f.url} onChange={set('url')} /></div>
          <div className="field span-2"><label htmlFor="ev-desc">Description <span className="muted">(themes, agencies, programs; drives tagging)</span></label><textarea id="ev-desc" className="textarea" value={f.description} onChange={set('description')} /></div>
          <div className="field span-2"><label>Technology domains</label><div className="tag-list">{tags.map((t) => <button type="button" key={t.id} className={`tag-toggle ${picked.includes(t.id) ? 'on' : ''}`} onClick={() => setPicked(picked.includes(t.id) ? picked.filter((x) => x !== t.id) : [...picked, t.id])}>{t.label}</button>)}</div></div>
          {dateField('registration_opens_on', 'Registration opens')}{dateField('registration_closes_on', 'Registration closes')}
          {dateField('abstract_due_on', 'Abstracts due')}{dateField('speaker_due_on', 'Speaker proposals due')}{dateField('exhibitor_due_on', 'Exhibitor deadline')}
        </div>
        {err && <div className="error-text">{err}</div>}
        <div className="form-actions"><button type="button" className="ghost-btn" onClick={onClose}>Cancel</button><button type="submit" className="primary-btn" disabled={saving || f.name.trim().length < 3}>{saving ? 'Saving…' : existing ? 'Save changes' : 'Add event'}</button></div>
      </form>
    </Modal>
  );
}

function EventModal({ id, me, tags, onClose, onChanged }: { id: string; me: Me; tags: Tag[]; onClose: () => void; onChanged: () => void }) {
  const [d, setD] = useState<EventDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState('');
  const canEdit = me.role !== 'viewer';
  const load = useCallback(() => { api<EventDetail>(`/api/events/${id}`).then(setD).catch((e: Error) => setErr(e.message)); }, [id]);
  useEffect(load, [load]);
  async function addNote(e: FormEvent) { e.preventDefault(); if (!note.trim()) return; try { await api(`/api/events/${id}/notes`, { method: 'POST', json: { body: note.trim() } }); setNote(''); load(); } catch (ex) { setErr((ex as Error).message); } }

  const deadlines = d ? ([['Registration opens', d.registration_opens_on], ['Registration closes', d.registration_closes_on], ['Abstracts due', d.abstract_due_on], ['Speaker proposals due', d.speaker_due_on], ['Exhibitor deadline', d.exhibitor_due_on], ['Event starts', d.starts_on]] as [string, string | null][]).filter(([, v]) => v) : [];
  return (
    <Modal title={d?.name ?? 'Loading…'} onClose={onClose}>
      {err && <div className="error-text">{err}</div>}
      {d && !editing && (
        <>
          <div className="filter-bar" style={{ marginBottom: 12 }}>
            <Pill kind="info">{kindLabel(d.kind)}</Pill>
            <Pill kind={d.status === 'cancelled' ? 'muted' : d.status === 'confirmed' ? 'ok' : 'warn'}>{titleCase(d.status)}</Pill>
            {(d.city || d.state) && <Pill kind="muted">{[d.city, d.state].filter(Boolean).join(', ')}</Pill>}
            <TagChips tags={d.tags} all={tags} max={8} />
            {d.url && <a className="link" href={d.url} target="_blank" rel="noreferrer">Event site ↗</a>}
            {canEdit && <button className="ghost-btn" style={{ marginLeft: 'auto' }} onClick={() => setEditing(true)}>Edit</button>}
          </div>
          {d.description && <p style={{ fontSize: 13, marginBottom: 12 }}>{d.description}</p>}
          <dl className="kv">
            <dt>When</dt><dd>{d.starts_on ? `${fmtDate(d.starts_on)}${d.ends_on && d.ends_on !== d.starts_on ? ` to ${fmtDate(d.ends_on)}` : ''}` : 'TBD'}</dd>
            <dt>Organizer</dt><dd>{d.organizer_name ?? '—'}</dd>
          </dl>
          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Deadlines</h3>
          {deadlines.length === 0 && <div className="muted sm">No deadlines recorded.</div>}
          {deadlines.map(([label, v]) => { const days = daysUntil(v); return <div className="version" key={label}><time>{fmtDate(v)}</time><span>{label}{days !== null && <span className={`sm ${days < 0 ? 'muted' : days <= 14 ? 'neg' : 'muted'}`}> · {days < 0 ? `${-days} days ago` : `in ${days} days`}</span>}</span></div>; })}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Recommendations by client ({d.recommendations.length})</h3>
          {d.recommendations.length === 0 && <div className="muted sm">No client profile scores against this event (no shared technology tag, agency or capability).</div>}
          {d.recommendations.map((r) => (
            <div className="version" key={r.id} style={{ alignItems: 'flex-start' }}>
              <time><ScoreBadge score={r.score} /></time>
              <span style={{ flex: 1 }}>
                <a className="link" href={`#/clients/${r.client_id}`}>{r.client_name}</a> <Pill kind={ACTION_KIND[r.rationale.action] ?? 'muted'}>{r.rationale.action}</Pill>{r.rationale.also.length > 0 && <span className="muted sm"> also {r.rationale.also.join(', ')}</span>}
                <span className="muted sm rationale">{r.rationale.why}{r.rationale.tags.length ? ` · ${r.rationale.tags.map((t) => tags.find((x) => x.id === t.tag_id)?.label ?? t.tag_id).join(', ')}` : ''}{r.rationale.agency.matched ? ` · ${titleCase(r.rationale.agency.matched.toLowerCase())}` : ''}</span>
              </span>
              {canEdit ? <DecisionButtons matchId={r.id} decision={r.decision} onChanged={() => { load(); onChanged(); }} /> : r.decision !== 'pending' && <DecisionPill decision={r.decision} />}
            </div>
          ))}

          <h3 style={{ fontSize: 13, marginTop: 18, marginBottom: 6 }}>Notes ({d.notes.length})</h3>
          {d.notes.map((n) => <div className="note" key={n.id}><div>{n.body}</div><div className="meta">{n.author ?? 'Unknown'} · {fmtDateTime(n.created_at)}</div></div>)}
          {canEdit && (
            <form onSubmit={addNote} style={{ marginTop: 10 }}>
              <div className="field"><textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Who is attending, meetings to schedule, what we learned…" /></div>
              <div className="form-actions"><button className="primary-btn" type="submit" disabled={!note.trim()}>Save note</button></div>
            </form>
          )}
        </>
      )}
      {d && editing && <EventForm tags={tags} existing={d} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); onChanged(); }} />}
    </Modal>
  );
}
